import { Link } from 'react-router-dom';
import { DocSection, kbd } from '../../../pages/shell';
import { DocShot } from '../../DocShot';

export const toc = [
  { id: 'overview', label: 'Overview' },
  { id: 'how-it-works', label: 'How it works' },
  { id: 'chips', label: 'The chips: what Ultra understood' },
  { id: 'refine', label: 'Refine without re-parsing' },
  { id: 'vram', label: 'Ultra needs VRAM' },
  { id: 'turn-it-on', label: 'Turn it on' },
];

export default function Body() {
  return (
    <>
      <DocSection id="overview" title="Overview">
        <p>
          Ultra Search fuses everything ClipRadar knows about your footage — what's on screen,
          what's said, and who's in frame — into a single search. Type one plain-language
          sentence and Ultra reads it, works out which signals it needs, and hands back one ranked
          list instead of you running Visuals, Speech, and People searches separately.
        </p>
        <DocShot
          slug="ultra-search"
          shot="chips"
          alt="The chips read-back under the search bar"
          caption="Every Ultra search shows its work: a row of chips under the bar explains exactly what it understood from your sentence."
        />
      </DocSection>

      <DocSection id="how-it-works" title="How it works">
        <p>
          Turn on Ultra and your sentence goes through a local <strong>query parser model</strong> —
          a small language model that runs on your own GPU, not in the cloud (see{' '}
          <Link to="/docs/concepts/local-and-private">Local &amp; private</Link>). The parser breaks
          your sentence into structured facets — who should be in the shot, what's said, what
          the frame looks like, anything to exclude, and filters like date, length, or file path —
          then ClipRadar runs every matching engine (Visuals, Speech, People) at once and fuses the
          rankings together, so a scene that scores well on more than one signal rises to the top.
        </p>
      </DocSection>

      <DocSection id="chips" title="The chips: what Ultra understood">
        <p>
          Ultra never works as a black box. Under the search bar it always lays out its
          interpretation as a row of <strong>chips</strong> — one per facet it pulled out of your
          sentence:
        </p>
        <ul>
          <li><strong>Person</strong> — who should be in the shot</li>
          <li><strong>Framing</strong> — click to cycle close-up → medium → wide</li>
          <li><strong>Looks</strong> — the visual description Ultra will match against frames</li>
          <li>
            <strong>Says</strong> — the speech query, with a quote/topic toggle for exact words vs.
            the general topic
          </li>
          <li><strong>Not</strong> — anything Ultra is told to exclude</li>
          <li>
            <strong>Top</strong>, <strong>File date</strong>, <strong>Length</strong>, and{' '}
            <strong>Path</strong> — result-limit and filter facets
          </li>
        </ul>
        <p>
          Click any chip to edit it, or × to remove it — either way ClipRadar re-runs the fused
          search immediately, without sending your sentence back through the parser. When Ultra had
          to guess — a pronoun it couldn't resolve, a named person it couldn't match, a
          facet it dropped because your GPU forced Lite mode — it adds an amber warning chip
          explaining why. More than three collapse into a single "+N notes" chip you can
          expand.
        </p>
      </DocSection>

      <DocSection id="refine" title="Refine without re-parsing">
        <p>
          <strong>Refine</strong> lets you keep steering a search instead of starting over. Type a
          follow-up instruction into the Refine row — "make it night", "just the wide
          shots" — and Ultra re-parses only that instruction on top of your current chips,
          rather than your whole original sentence. Suggestion chips underneath teach you the kind
          of language Ultra understands, and <strong>Undo refine</strong> restores the chips you had
          before. In the Premiere panel, Refine tucks behind a compact{' '}
          <span style={kbd}>✦ Refine</span> pill to save space.
        </p>
        <DocShot
          slug="ultra-search"
          shot="refine"
          alt="The refine row under Ultra results"
          caption="A follow-up instruction re-parses on top of the current chips — your first search isn't thrown away."
        />
      </DocSection>

      <DocSection id="vram" title="Ultra needs VRAM">
        <p>
          Ultra keeps a language model warm on your GPU, so its availability tracks your GPU the
          same way analysis speed does — see{' '}
          <Link to="/docs/concepts/models-and-your-gpu">AI models &amp; your GPU</Link>. The{' '}
          <span style={kbd}>✦</span> pill is always honest about its state:
        </p>
        <ul>
          <li><strong>Ready</strong> — Ultra is loaded and usable</li>
          <li>
            <strong>Get</strong> — the model isn't downloaded yet; click through to Settings to
            fetch it
          </li>
          <li><strong>Downloading</strong> — a live percentage shown right on the pill</li>
          <li>
            <strong>Unavailable</strong> — dimmed, with the reason in a tooltip (a non-CUDA GPU, or
            too little VRAM free right now)
          </li>
        </ul>
        <p>
          On GPUs under roughly 12 GB of VRAM, a <strong>Lite</strong> tag appears — a smaller,
          slightly less accurate parser model. ClipRadar still loads the full model opportunistically
          whenever memory frees up, for example after you close Premiere. If Ultra genuinely
          can't run, it never fails silently: a fallback notice explains why, and your plain
          Visuals/Speech/People search results stand in.
        </p>
        <DocShot
          slug="ultra-search"
          shot="pill-states"
          alt="Ultra pill states (ready, Lite, unavailable)"
          caption="The ✦ pill always tells you which state Ultra is in — never a silent switch to plain search."
        />
      </DocSection>

      <DocSection id="turn-it-on" title="Turn it on">
        <p>
          Turn Ultra on by clicking the <span style={kbd}>✦</span> pill next to the search bar, or
          type <span style={kbd}>/find</span> before your query — <span style={kbd}>Esc</span> exits
          back to a plain search. The first time, Ultra shows a <span style={kbd}>Get</span> state
          until you download its query parser model from{' '}
          <Link to="/docs/interface/settings">Settings → Ultra Search</Link>; after that it stays
          loaded and ready for every session.
        </p>
        <DocShot
          slug="ultra-search"
          shot="download-cta"
          alt="The Ultra Search model download control in Settings"
          caption="Download the query parser model once from Settings — Ultra stays ready after that."
        />
      </DocSection>
    </>
  );
}
